import React, { useEffect, useState } from "react";
import { Container } from "shards-react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import axios from "axios";
import { NavLink } from "react-router-dom";
import guardianImage from "../../assets/dashboard/guardian.png";
import { Cloud_API_URL } from "../../utils/constants";
import sdImage from "../../assets/dashboard/sd.svg";
import csdImage from "../../assets/dashboard/csd.svg";
import dbImage from "../../assets/dashboard/db.svg";
import threePlusImage from "../../assets/dashboard/3+.svg";
import eolImage from "../../assets/dashboard/eol.svg";
import changesImage from "../../assets/dashboard/changes.svg";
import moment from "moment";
import { getAll } from "firebase/remote-config";
import { useAuth } from "../../providers/AuthProvider";
import { firebaseRemoteConfig } from "../../firebase";
import "./users.css";
import { updateStarredFlag } from "../../utils/api_calls";
import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  FormControlLabel,
} from "@mui/material";

const SimpleUsersTable = () => {
  const availableFilters = [
    "all",
    "Training",
    "Guardian",
    "performedTests",
    "child",
    "student",
    "starred",
  ];
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("last_training_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [remoteConfigData, setRemoteConfigData] = useState(null);
  const { currentUser } = useAuth();
  const [filterTypes, setFilterTypes] = useState([...availableFilters]);
  const [debouncedFilterTypes, setDebouncedFilterTypes] = useState(filterTypes);

  // State for excluding test accounts
  const [excludeTestAccounts, setExcludeTestAccounts] = useState(false);

  useEffect(() => {
    if (currentUser) {
      getRemoteConfigs();
    }
  }, [currentUser]);

  const getRemoteConfigs = () => {
    const data = getAll(firebaseRemoteConfig);
    setRemoteConfigData(data);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setPage(0);
  };

  useEffect(() => {
    const getUsers = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${Cloud_API_URL}/user/all-users?page=${
            page + 1
          }&limit=${rowsPerPage}&search=${debouncedSearch}&sortBy=${sortBy}&sortOrder=${sortOrder}&filterType=${debouncedFilterTypes.join(
            ","
          )}&excludeTestAccounts=${excludeTestAccounts}`
        );
        const { users, total } = response.data;
        const formattedUsers = users.map((user) => {
          let arrow = "—";
          if (user.latest_average > user.previous_three_average) {
            arrow = "↑";
          } else if (user.latest_average < user.previous_three_average) {
            arrow = "↓";
          }
          return {
            id: user.user_id,
            uuid: user.user_uuid,
            registeredOn: user.registered_on,
            email: user.email || "-",
            firstName: user.first_name || "-",
            lastName: user.last_name || "-",
            latestAverage: user.latest_average || "-",
            previousThreeAverage: user.previous_three_average || "-",
            arrow:
              user.latest_average && user.previous_three_average ? arrow : "—",
            standardDeviation: user.standard_deviation || "-",
            calibrationStandardDeviation:
              user.calibration_standard_deviation || "-",
            firstTrainingDate: user.first_training_date || "-",
            lastTrainingDate: user.last_training_date || "-",
            nextTrainingDate: user.next_training_date || "-",
            vergenceTestCount: user.vergence_test_count || "-",
            validTill: user.valid_until || "-",
            changeFlag: user.change_flag || false,
            readFromBackend: user.read_from_backend || false,
            performedTestToday: user.performed_test_today,
            hasDoubleVision: user.has_double_vision,
            type: user.type || "-",
            starred: user.starred || false,
            isTestAccount: user.is_test_account || false,
          };
        });
        setUsers(formattedUsers);
        setTotalUsers(total);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    getUsers();
  }, [
    page,
    rowsPerPage,
    debouncedSearch,
    sortBy,
    sortOrder,
    debouncedFilterTypes,
    excludeTestAccounts,
  ]);

  // Debounce the search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterTypes(filterTypes);
    }, 1500);

    return () => {
      clearTimeout(handler);
    };
  }, [filterTypes]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleStarClick = async (user) => {
    try {
      // Toggle the current starred value for the given user
      const newStarredValue = !user.starred;
      await updateStarredFlag(user.uuid, newStarredValue);
      setUsers((prevUsers) =>
        prevUsers.map((u) =>
          u.uuid === user.uuid ? { ...u, starred: newStarredValue } : u
        )
      );
    } catch (error) {
      console.error(
        `Failed to update starred flag for user ${user.uuid}:`,
        error
      );
    }
  };

  const handleTestAccountClick = async (event, user) => {
    // Prevent row click propagation
    event.stopPropagation();

    // Optimistic Update
    const previousState = [...users];
    const newTestAccountValue = !user.isTestAccount;

    setUsers((prevUsers) =>
      prevUsers.map((u) =>
        u.uuid === user.uuid ? { ...u, isTestAccount: newTestAccountValue } : u
      )
    );

    try {
      await axios.put(`${Cloud_API_URL}/user/update-test-account-flag`, {
        uuid: user.uuid,
        isTestAccount: newTestAccountValue,
      });
    } catch (error) {
      console.error(
        `Failed to update test account flag for user ${user.uuid}:`,
        error
      );
      // Revert UI on error
      setUsers(previousState);
      alert("Failed to update test account status.");
    }
  };

  const handleChange = (event) => {
    const newSelection = event.target.value; // array of selected filters
    let finalSelection;

    if (!filterTypes.includes("all") && newSelection.includes("all")) {
      finalSelection = [...availableFilters];
    } else if (filterTypes.includes("all") && !newSelection.includes("all")) {
      finalSelection = [];
    } else if (
      newSelection.includes("all") &&
      newSelection.length !== availableFilters.length
    ) {
      finalSelection = newSelection.filter((v) => v !== "all");
    } else {
      finalSelection = newSelection;
    }

    setFilterTypes(finalSelection);
  };

  // Search bar component
  const searchBar = () => {
    return (
      <input
        className="form-control"
        type="text"
        value={search}
        onChange={handleSearchChange}
        placeholder="Search..."
        style={{
          width: "200px",
          padding: "8px",
          fontSize: "14px",
          borderRadius: "5px",
          border: "1px solid #ccc",
        }}
      />
    );
  };

  return (
    <Container fluid className="main-content-container px-6">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h6 className="m-0 font-weight-bold" style={{ fontSize: "24px" }}>
          Users
        </h6>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* New Checkbox for Excluding Test Accounts */}
          <FormControlLabel
            control={
              <Checkbox
                checked={excludeTestAccounts}
                onChange={(e) => {
                  setExcludeTestAccounts(e.target.checked);
                  setPage(0); // Reset page when filtering
                }}
                color="primary"
              />
            }
            label="Exclude Test Accounts"
          />

          <FormControl sx={{ width: "200px", height: "40px" }}>
            <InputLabel id="filter-select-label">Filter by Type</InputLabel>
            <Select
              labelId="filter-select-label"
              multiple
              value={filterTypes}
              onChange={handleChange}
              input={
                <OutlinedInput label="Filter by Type" sx={{ height: "40px" }} />
              }
              renderValue={(selected) =>
                selected.length === availableFilters.length
                  ? "all"
                  : selected.join(", ")
              }
            >
              {availableFilters.map((filter) => (
                <MenuItem key={filter} value={filter}>
                  <Checkbox checked={filterTypes.includes(filter)} />
                  <ListItemText primary={filter} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {searchBar()}
        </div>
      </div>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        {loading ? (
          <div
            style={{
              height: "80vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="spinner">
              <div className="spinner-1"></div>
              <div className="spinner-2"></div>
              <div className="spinner-3"></div>
            </div>
          </div>
        ) : (
          <TableContainer sx={{ maxHeight: "80vh", overflowY: "auto" }}>
            <Table stickyHeader aria-label="simple table">
              <TableHead
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background: "#fff",
                }}
              >
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      top: 0,
                      background: "#fff",
                      zIndex: 2,
                      width: "5%",
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                    }}
                  >
                    #
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("first_name")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Name{" "}
                    {sortBy === "first_name" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("first_training_date")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                      minWidth: "100px",
                    }}
                  >
                    First T.D{" "}
                    {sortBy === "first_training_date" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("last_training_date")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                      minWidth: "100px",
                    }}
                  >
                    Last T.D{" "}
                    {sortBy === "last_training_date" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("next_training_date")}
                    style={{
                      borderRight: "1px solid #d3d3d3", // Add border here
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                      minWidth: "100px",
                    }}
                  >
                    Next Test{" "}
                    {sortBy === "next_training_date" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>

                  <TableCell
                    align="center"
                    onClick={() => handleSort("email")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Email{" "}
                    {sortBy === "email" && (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("registered_on")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                      minWidth: "150px",
                    }}
                  >
                    Registered On{" "}
                    {sortBy === "registered_on" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("latest_average")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Calibration Score{" "}
                    {sortBy === "latest_average" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("standard_deviation")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Standard Deviation
                    {sortBy === "standard_deviation" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    onClick={() => handleSort("calibration_standard_deviation")}
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      cursor: "pointer",
                    }}
                  >
                    Calibration SD
                    {sortBy === "calibration_standard_deviation" &&
                      (sortOrder === "asc" ? "↑" : "↓")}
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      position: "relative",
                    }}
                  >
                    Action
                    <div className="tooltip-container">
                      <i
                        className="material-icons tooltip-icon"
                        style={{
                          fontSize: "16px",
                          cursor: "pointer",
                          color: "#666",
                        }}
                      >
                        info
                      </i>
                      <div className="tooltip-content">
                        <ul
                          style={{
                            margin: 0,
                            padding: 0,
                            listStyleType: "none",
                          }}
                        >
                          <li>
                            <img
                              src={sdImage}
                              title={"Training Standard Deviation"}
                              className="actionIcon"
                              alt="tsdIcon"
                            />{" "}
                            - Training Standard Deviation
                          </li>
                          <li>
                            <img
                              src={csdImage}
                              title={"Calibration Standard Deviation"}
                              className="actionIcon"
                              alt="csdIcon"
                            />{" "}
                            - Calibration Standard Deviation
                          </li>
                          <li>
                            <img
                              src={dbImage}
                              title={"Read Calibration from Backend"}
                              className="actionIcon"
                              alt="rdbIcon"
                            />{" "}
                            - Read from backend
                          </li>
                          <li>
                            <img
                              src={threePlusImage}
                              title={
                                "Latest Training Date is More Than 3 Days Ago"
                              }
                              className="actionIcon"
                              alt="trainingDateIcon"
                            />{" "}
                            - Didn't train for 3 or more days
                          </li>
                          <li>
                            <img
                              src={eolImage}
                              title={"Expired"}
                              className="actionIcon"
                              alt="expired"
                            />{" "}
                            - End of lifetime
                          </li>
                          <li>
                            <img
                              src={changesImage}
                              title={"Changes Made"}
                              className="actionIcon"
                              alt="changedMadeIcon"
                            />{" "}
                            - Changes in User DB
                          </li>
                          <li>
                            <span
                              className="actionIconText"
                              title={"Latest Vergence Test Number"}
                            >
                              1
                            </span>{" "}
                            - Latest vergence test number
                          </li>
                        </ul>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                    }}
                  >
                    Starred
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                      minWidth: "80px",
                    }}
                  >
                    Test Acc.
                  </TableCell>
                  <TableCell
                    align="center"
                    style={{
                      borderRight: "1px solid #d3d3d3",
                      borderBottom: "2px solid #d3d3d3",
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "4px",
                    }}
                  >
                    Statistics
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length > 0 ? (
                  users.map((user, index) => (
                    <TableRow
                      key={user.id}
                      style={{
                        height: "50px",
                        backgroundColor: user.hasDoubleVision
                          ? "orange"
                          : user.performedTestToday
                          ? "lightblue"
                          : undefined,
                      }}
                    >
                      <TableCell
                        align="center"
                        style={{
                          width: "5%",
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "10px",
                        }}
                      >
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "10px",
                        }}
                      >
                        <NavLink
                          to={`/user-details/${user.uuid}`}
                          style={{
                            color: "inherit",
                            fontSize: "0.9em",
                            fontWeight: 500,
                            padding: 0,
                            wordWrap: "break-word",
                            textAlign: "center",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${user.firstName} ${user.lastName}`}
                          {user.UserType && user.UserType === "Guardian" && (
                            <img
                              className="guardianIcon"
                              src={guardianImage}
                              alt="Guardian"
                              style={{ marginLeft: "5px" }}
                            />
                          )}
                        </NavLink>
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "20px",
                        }}
                      >
                        {user.firstTrainingDate !== "-"
                          ? new Date(
                              user.firstTrainingDate
                            ).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "20px",
                          color: (() => {
                            if (user.lastTrainingDate !== "-") {
                              const lastTrainingDate = new Date(
                                user.lastTrainingDate
                              );
                              const today = new Date();

                              const todayDateOnly = new Date(
                                today.setHours(0, 0, 0, 0)
                              );
                              const lastTrainingDateOnly = new Date(
                                lastTrainingDate.setHours(0, 0, 0, 0)
                              );

                              const diffInDays = Math.floor(
                                (todayDateOnly - lastTrainingDateOnly) /
                                  (1000 * 60 * 60 * 24)
                              );

                              if (diffInDays === 0) {
                                return "green"; // Today
                              } else if (diffInDays >= 1 && diffInDays <= 2) {
                                return "orange"; // Yesterday (1 day ago)
                              } else if (diffInDays >= 3 && diffInDays <= 6) {
                                return "purple"; // Between 2 and 5 days ago
                              } else {
                                return "red"; // Older than 5 days
                              }
                            }
                            return "inherit"; // Default color if no valid date
                          })(),
                        }}
                      >
                        {user.lastTrainingDate !== "-"
                          ? new Date(user.lastTrainingDate).toLocaleDateString()
                          : "-"}
                      </TableCell>

                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "20px",
                        }}
                      >
                        {user.nextTrainingDate !== "-"
                          ? new Date(user.nextTrainingDate).toLocaleDateString()
                          : "-"}
                      </TableCell>

                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "4px",
                        }}
                      >
                        {user.email}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "4px",
                        }}
                      >
                        {new Date(user.registeredOn).toLocaleDateString()}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.latestAverage !== "-" ? (
                          <>
                            <span>
                              {parseFloat(user.latestAverage).toFixed(2)}
                            </span>{" "}
                            <span
                              style={{
                                marginLeft: "5px",
                                fontSize: "14px",
                                verticalAlign: "middle",
                                display: "inline-block",
                              }}
                            >
                              {user.arrow}
                            </span>
                          </>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.standardDeviation !== "-"
                          ? parseFloat(user.standardDeviation).toFixed(2) + "%"
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "4px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.calibrationStandardDeviation !== "-"
                          ? parseFloat(
                              user.calibrationStandardDeviation
                            ).toFixed(2) + "%"
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                        }}
                      >
                        <div
                          className="actionIconContainer centerItems actionColumn"
                          style={{ height: "30px" }}
                        >
                          <div className="actionIconWrapper">
                            {user.standardDeviation !== 0 &&
                              remoteConfigData &&
                              remoteConfigData.Training_Standard_Deviation &&
                              parseFloat(user.standardDeviation) >
                                parseFloat(
                                  remoteConfigData.Training_Standard_Deviation
                                    ._value
                                ) && (
                                <img
                                  src={sdImage}
                                  title={"Training Standard Deviation"}
                                  className="actionIcon"
                                  alt="tsdIcon"
                                />
                              )}
                          </div>
                          <div className="actionIconWrapper">
                            {user.calibrationStandardDeviation !== 0 &&
                              remoteConfigData &&
                              remoteConfigData.Calibration_Standard_Deviation &&
                              parseFloat(user.calibrationStandardDeviation) >
                                parseFloat(
                                  remoteConfigData
                                    .Calibration_Standard_Deviation._value
                                ) && (
                                <img
                                  src={csdImage}
                                  title={"Calibration Standard Deviation"}
                                  className="actionIcon"
                                  alt="csdIcon"
                                />
                              )}
                          </div>

                          <div className="actionIconWrapper">
                            {user.readFromBackend && (
                              <img
                                src={dbImage}
                                title={"Read Calibration from Backend"}
                                className="actionIcon"
                                alt="rdbIcon"
                              />
                            )}
                          </div>
                          <div className="actionIconWrapper">
                            {user.lastTrainingDate &&
                            moment(user.lastTrainingDate).isBefore(
                              moment().subtract(3, "days")
                            ) ? (
                              <img
                                src={threePlusImage}
                                title={
                                  "Latest Training Date is More Than 3 Days Ago"
                                }
                                className="actionIcon"
                                alt="trainingDateIcon"
                              />
                            ) : null}
                          </div>
                          <div className="actionIconWrapper">
                            {user.validTill &&
                            moment(new Date(user.validTill)).isBefore() ? (
                              <img
                                src={eolImage}
                                title={"Expired"}
                                className="actionIcon"
                                alt="expired"
                              />
                            ) : (
                              ""
                            )}
                          </div>
                          <div className="actionIconWrapper">
                            {user.changeFlag && user.changeFlag && (
                              <img
                                src={changesImage}
                                title={"Changes Made"}
                                className="actionIcon"
                                alt="changedMadeIcon"
                              />
                            )}
                          </div>
                          <div className="actionIconWrapper">
                            {user.vergenceTestCount >= 1 && (
                              <span
                                className="actionIconText"
                                title={"Latest Vergence Test Number"}
                              >
                                {user.vergenceTestCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "10px",
                        }}
                      >
                        <div className="rating centerItems">
                          <span
                            className={user.starred ? "starred" : ""}
                            onClick={() => handleStarClick(user)}
                            style={{ cursor: "pointer" }}
                          >
                            ☆
                          </span>
                        </div>
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                          padding: "4px",
                        }}
                      >
                        <Checkbox
                          checked={user.isTestAccount}
                          onChange={(e) => handleTestAccountClick(e, user)}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell
                        align="center"
                        style={{
                          borderRight: "1px solid #d3d3d3",
                          fontSize: "12px",
                        }}
                      >
                        <a
                          className="centerItems"
                          target={"_blank"}
                          rel="noreferrer"
                          href={`https://statistics-imvi.web.app/Answers?Email=${encodeURIComponent(
                            user.email
                          )}`}
                        >
                          <img
                            src={require("../../assets/dashboard/statistics.png")}
                            alt={"Report"}
                            title={"User Answers Report"}
                            style={{ width: "30px" }}
                          />
                        </a>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={8}
                      style={{ padding: "8px" }}
                    >
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[50, 100, 200]}
              component="div"
              count={totalUsers}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                position: "sticky",
                bottom: 0,
                background: "#fff",
                zIndex: 2,
                borderTop: "1px solid #d3d3d3",
              }}
            />
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default SimpleUsersTable;
